import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@material-ui/core';
import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import {
  Award,
  ChevronDown,
  X as CloseIcon,
  Cpu,
  CreditCard,
  Eye,
  GitMerge,
  Globe,
  Mail,
  Menu as MenuIcon,
  ShoppingCart,
  Target,
  Zap
} from 'react-feather';
import { CALENDAR_LINK } from '../../constants/demo-link';
import ENV from '../../env.json';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { CtaButton } from '../CtaButton';
import { GetStartedDialog } from '../GetStartedDialog';
import {
  ComingSoonBadge,
  EarlyAccessBadge,
  NewBadge
} from '../NewBadge';

const NavBarInner = styled('div')`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  align-items: center;
  color: ${COLORS.TEXT};
  transition: 0.7s;
  padding: 0 ${(p) => p.theme.spacing(2)}px;

  a {
    transition: none;
  }

  @media (max-width: 800px) {
    padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(1)}px;
    grid-template-columns: 90px 1fr;
  }
`;

const Menu = styled('ul')`
  list-style-type: none;
  padding: 0;
  margin: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  color: ${COLORS.TEXT};
  text-align: center;
`;

const MenuItem = styled('li')`
  display: inline-block;
  padding: 24px 12px;

  @media (max-width: 800px) {
    padding: 12px;
  }

  a {
    font-size: 16px;
    position: relative;
  }

  a:before {
    content: '';
    position: absolute;
    display: block;
    width: 100%;
    height: 2px;
    bottom: -6px;
    left: 0;
    background-color: #000;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  a:hover:before {
    transform: scaleX(1);
  }
`;

const RightColumn = styled('div')`
  text-align: right;
  position: relative;
  top: 2px;

  @media (max-width: 800px) {
    font-size: 12px;
    top: 0;
  }

  a {
    position: relative;

    &.MuiButton-root:before {
      background-color: transparent;
      width: 0;
      transform: none;
    }

    &.MuiButton-root {
      transform: scale(1);
      transition: transform 0.1s ease;
    }
    &.MuiButton-root:hover {
      transform: scale(1.1);
    }
  }

  a:before {
    content: '';
    position: absolute;
    display: block;
    width: 100%;
    height: 2px;
    bottom: -6px;
    left: 0;
    background-color: #000;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  a:hover:before {
    transform: scaleX(1);
  }

  .hide-on-mobile {
    @media (max-width: 800px) {
      display: none !important;
    }
  }

  .hide-on-desktop {
    display: none;

    @media (max-width: 800px) {
      display: inline-block;
    }
  }
`;

const SubMenuContainer = styled(MenuItem)`
  position: relative;
  display: inline-block;
  transition: 0.1s opacity linear;

  &:hover .submenu,
  &:hover .submenu-spanned {
    display: grid;
    opacity: 1;
    transition: 0.1s opacity linear;
    z-index: 100;
    background-color: #fff;
    pointer-events: auto;
  }

  .submenu:hover,
  .submenu-spanned:hover {
    display: grid;
    opacity: 1;
    z-index: 100;
    pointer-events: auto;
  }

  .submenu {
    display: none;
    top: 56px;
    opacity: 0;
    z-index: -1;
    position: absolute;
    transform: translate(-130px, 0px);
    border-radius: ${(p) => p.theme.shape.borderRadius}px;
    z-index: 1;
    color: ${COLORS.TEXT};
    width: 700px;
    margin: 0;
    padding-inline-start: 0;
    box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.2);
    border: 1px solid ${COLORS.PAPER};
    overflow: hidden;
    grid-template-columns: 1fr 1fr;
    pointer-events: none;

    li {
      list-style-type: none;
      text-align: left;

      a {
        display: flex;
        align-items: center;
        padding: ${(p) => p.theme.spacing(2)}px;

        svg {
          margin-right: 12px;
        }

        &:hover {
          background-color: #e6f7ff;
          .link-title {
            color: ${COLORS.PRIMARY};
          }
        }

        &:before {
          display: none;
        }
      }
    }
  }

  .submenu-spanned {
    display: none;
    top: 56px;
    opacity: 0;
    z-index: -1;
    position: absolute;
    transform: translate(-130px, 0px);
    border-radius: ${(p) => p.theme.shape.borderRadius}px;
    z-index: 1;
    color: ${COLORS.TEXT};
    width: 700px;
    margin: 0;
    padding-inline-start: 0;
    box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.2);
    border: 1px solid ${COLORS.PAPER_DARK};
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0;
    pointer-events: none;

    li {
      list-style-type: none;
      text-align: left;

      a {
        display: flex;
        align-items: center;
        padding: ${(p) => p.theme.spacing(2)}px;

        svg {
          margin-right: 12px;
        }

        &:hover {
          background-color: #e6f7ff;

          .link-title {
            color: ${COLORS.PRIMARY};
          }
        }

        &:before {
          display: none;
        }
      }

      &:nth-child(1) {
        grid-column: 1;
        grid-row: 1;
      }

      &:nth-child(2) {
        grid-column: 1;
        grid-row: 2;
      }

      &.span-right {
        grid-column: 2;
        grid-row: 1 / 3;
        align-self: center;
      }
    }
  }
`;

const LinkTitle = styled('span')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`;

const LinkDescription = styled('span')`
  margin-top: 6px;
  color: #666;
  display: block;
  font-size: 14px;
  max-width: 300px;
`;

const MenuLinks = () => {
  const isMobile = useMediaQuery('(max-width: 800px)');

  return (
    <Menu>
      <MenuItem>
        <Link to="/">Home</Link>
      </MenuItem>
      <SubMenuContainer>
        <Link to="/product/">
          Product &nbsp;
          {!isMobile && (
            <ChevronDown
              size={18}
              style={{ position: 'relative', top: '4px' }}
            />
          )}
        </Link>
        {!isMobile && (
          <ul className="submenu">
            <li
              style={{
                gridColumn: '1 / -1',
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              <Link to="/ai-platform/">
                <Cpu size={18} />
                <div style={{ width: '100%', overflow: 'hidden' }}>
                  <LinkTitle className="link-title">
                    AI Platform
                    <EarlyAccessBadge />
                  </LinkTitle>
                  <LinkDescription style={{ maxWidth: '100%' }}>
                    Affilimate Agent, MCP, and agentic campaign management —
                    one AI layer across everything you monetize.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/product/unify/">
                <GitMerge size={18} />
                <div>
                  <LinkTitle className="link-title">Unify</LinkTitle>
                  <LinkDescription>
                    All your affiliate data from 100+ networks in one place —
                    transactions, rates, finance data, and payouts.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/product/optimize/">
                <Target size={18} />
                <div>
                  <LinkTitle className="link-title">Optimize</LinkTitle>
                  <LinkDescription>
                    The content optimization solution for editorial teams:
                    heatmaps, offsite tracking, and channel insights.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/product/amplify/">
                <Zap size={18} />
                <div>
                  <LinkTitle className="link-title">
                    Amplify
                    <NewBadge>New</NewBadge>
                  </LinkTitle>
                  <LinkDescription>
                    Campaigns and Yield Optimizer — sponsored content, agentic
                    link swaps, and AI-suggested opportunities.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/ai-visibility/">
                <Eye size={18} />
                <div>
                  <LinkTitle className="link-title">
                    AI Visibility
                    <ComingSoonBadge />
                  </LinkTitle>
                  <LinkDescription>
                    Monetize your influence in AI answers with
                    publisher-first visibility campaigns.
                  </LinkDescription>
                </div>
              </Link>
            </li>
          </ul>
        )}
      </SubMenuContainer>
      <SubMenuContainer>
        <Link to="/use-cases/">
          Solutions &nbsp;
          {!isMobile && (
            <ChevronDown
              size={18}
              style={{ position: 'relative', top: '4px' }}
            />
          )}
        </Link>
        {!isMobile && (
          <ul className="submenu">
            <li>
              <Link to="/use-cases/?tab=news-media">
                <Globe size={18} />
                <div>
                  <LinkTitle className="link-title">
                    News and Media Publishers
                  </LinkTitle>
                  <LinkDescription>
                    Uncover exactly how readers interact, shop, and convert
                    across every article and traffic source.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/use-cases/?tab=newsletter">
                <Mail size={18} />
                <div>
                  <LinkTitle className="link-title">
                    Newsletter Publishers
                  </LinkTitle>
                  <LinkDescription>
                    Measure revenue per email, campaign, segment, or subscriber
                    and enrich your CRM with granular performance insights.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/use-cases/?tab=shopping-loyalty">
                <Award size={18} />
                <div>
                  <LinkTitle className="link-title">
                    Shopping & Loyalty Platforms
                  </LinkTitle>
                  <LinkDescription>
                    Power rewards, recommendations, and commerce experiences
                    with real-time affiliate data and automated link creation.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li>
              <Link to="/use-cases/?tab=finance">
                <CreditCard size={18} />
                <div>
                  <LinkTitle className="link-title">
                    Publisher Finance Teams
                  </LinkTitle>
                  <LinkDescription>
                    Track and reconcile your affiliate revenue from transaction
                    to payout in one unified view.
                  </LinkDescription>
                </div>
              </Link>
            </li>
            <li
              style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0' }}
            >
              <Link to="/demand/">
                {' '}
                <ShoppingCart size={18} />
                <div style={{ width: '100%', overflow: 'hidden' }}>
                  <LinkTitle className="link-title">
                    Brands & Agencies
                  </LinkTitle>
                  <LinkDescription style={{ maxWidth: '100%' }}>
                    Search millions of product mentions to discover
                    opportunities to grow your reach and revenue. Then,
                    collaborate with publishers to deploy sponsorship campaigns
                    at scale.
                  </LinkDescription>
                </div>
              </Link>
            </li>
          </ul>
        )}
      </SubMenuContainer>
      <MenuItem>
        <Link to="/integrations/">Integrations</Link>
      </MenuItem>
      <MenuItem>
        <Link to="/pricing/">Pricing</Link>
      </MenuItem>
      <MenuItem>
        <Link to={CALENDAR_LINK} target="_blank">
          Demo
        </Link>
      </MenuItem>
    </Menu>
  );
};

const DesktopToggle = styled('div')`
  display: inline-block;
  @media (max-width: 800px) {
    display: none;
  }
`;
const MobileToggle = styled('div')`
  display: none;

  @media (max-width: 800px) {
    display: inline-block;
  }
`;

const NavWrapper = styled('div')<{ scrolled: boolean }>`
  top: 0;
  position: fixed;
  z-index: 100;
  width: 100%;
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;

  ${(p) =>
    p.scrolled &&
    `
    background-color: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  `}
`;

const Navigation = () => {
  const isMobile = useMediaQuery('(max-width: 800px)');
  const [mobileMenu, toggleMobileMenu] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const seeMenu = (function () {
    if (!isMobile) {
      return false;
    }
    return mobileMenu;
  })();

  return (
    <NavWrapper scrolled={scrolled}>
      <NavBarInner>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <MobileToggle>
            <IconButton onClick={() => toggleMobileMenu(!mobileMenu)}>
              {!mobileMenu ? (
                <MenuIcon color="black" />
              ) : (
                <CloseIcon color="black" />
              )}
            </IconButton>
          </MobileToggle>
          <Link to="/">
            <img
              src="https://storage.googleapis.com/affilimate-assets/logos/logo-black-full.png"
              height="20"
              width="89"
              title="Affilimate Home Page"
              alt="Affilimate"
            />
          </Link>
        </div>
        <DesktopToggle>
          <MenuLinks />
        </DesktopToggle>
        <RightColumn>
          <Button
            variant="text"
            className="hide-on-mobile"
            onClick={(e) => {
              e.preventDefault();
              setLoginDialogOpen(true);
            }}
            style={{
              display: 'inline-block',
              marginLeft: '12px',
              marginRight: '18px',
              cursor: 'pointer'
            }}
          >
            Login
          </Button>
          <CtaButton size="small" onClick={() => setDialogOpen(true)}>
            Get a demo
          </CtaButton>
        </RightColumn>
      </NavBarInner>
      {seeMenu && <MenuLinks />}
      <GetStartedDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: 'center' }}>
          Log in to your account...
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              paddingTop: '8px'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              href={ENV.app.baseUrl}
            >
              Publisher Login
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              size="large"
              href="https://demand.affilimate.com"
            >
              Brand & Agency Login
            </Button>
          </div>
          <br />
        </DialogContent>
      </Dialog>
    </NavWrapper>
  );
};

export default Navigation;
